import APIHandlers from './api';
export const MODEL = 'gpt-realtime-mini';
export const MIC_CONSTRAINTS = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false }
};
// ======================================================
// CONNECTION MANAGEMENT
// ======================================================
class ConnectionManager {
  constructor(state, statusDisplay, emitEvent, mixer) {
    this.state = state;
    this.statusDisplay = statusDisplay;
    this.emitEvent = emitEvent;
    this.mixer = mixer;

    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    // Mic stream handed over by Avatar (granted before the session starts);
    // localStream is the one in use — both are stopped on disconnect.
    this.micStream = null;
    this.localStream = null;
    this.isConnected = false;
    this.isListening = false;
    this.isSpeaking = false;

    // Session config to include in the /calls request
    this.sessionConfig = null;
    // Optional async hook awaited right before /calls — lets the tour guide
    // settle the video-avatar result (text vs audio output) while the token,
    // mic and SDP offer are prepared in parallel. Sets this.sessionConfig.
    this.beforeCalls = null;
    this._gen = 0; // bumped by disconnect() so an in-flight connect() aborts
    this._dropTimer = null;
  }

  // ms a 'disconnected' peer connection may take to recover before the
  // session counts as lost (short network blips recover on their own)
  static DROP_GRACE_MS = 5000;

  // The network went away mid-session: without this the guide stayed silent
  // and the button stayed on Pause. Reported once, as a connection:error the
  // Avatar ends the session on.
  _onLost(reason) {
    clearTimeout(this._dropTimer);
    this._dropTimer = null;
    if (!this.isConnected) return;
    this.isConnected = false;
    console.warn('[connection] lost:', reason);
    this.emitEvent('connection:error', Object.assign(new Error(`connection lost (${reason})`), { lost: true }));
  }

  _watch(pc, dc, gen) {
    pc.onconnectionstatechange = () => {
      if (gen !== this._gen) return; // our own disconnect()
      const state = pc.connectionState;
      if (state === 'failed') {
        this._onLost('failed');
      } else if (state === 'disconnected') {
        clearTimeout(this._dropTimer);
        this._dropTimer = setTimeout(() => {
          if (gen === this._gen && pc.connectionState !== 'connected') this._onLost('disconnected');
        }, ConnectionManager.DROP_GRACE_MS);
      } else if (state === 'connected') {
        clearTimeout(this._dropTimer);
        this._dropTimer = null;
      }
    };
    dc.onclose = () => {
      if (gen === this._gen) this._onLost('data channel closed');
    };
  }

  async connect() {
    const gen = ++this._gen;
    try {
      this.emitEvent('connection:status', 'connecting');
      const kb_key = this.state.tourData?.kbSelect || '';
      const tour_id = this.state.tourData?.tourID || '';
      const company_id = this.state.tourData?.companyID || '';
      const tokenData = await APIHandlers.getToken(MODEL, this.state.tourData.ttsModel, kb_key, tour_id, company_id);
      if (tokenData.agent_disabled) {
        this.emitEvent('agent:disabled', {});
        return;
      }
      const { ephemeral_key } = tokenData;
      if (!ephemeral_key) throw new Error('Missing ephemeral key');

      this.peerConnection = new RTCPeerConnection();
      this.audioElement = document.createElement('audio');
      this.audioElement.autoplay = true;
      // The element exists only so browsers deliver the remote track into
      // WebAudio — the mixer owns the speaker path. It must never be audible
      // itself: iOS ignores volume writes and late muted changes on
      // MediaStream-backed elements (the voice played twice there), so mute
      // before anything starts playing.
      this.audioElement.muted = true;
      this.audioElement.volume = 0;

      this.peerConnection.ontrack = e => {
        const stream = e.streams[0];
        this.audioElement.srcObject = stream;
        this.audioElement.play().catch(() => {});
        if (this.mixer) this.mixer.connectAIStream(stream);
        this.emitEvent('connection:aistream', stream);
      };

      const stream = this.micStream || await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      this.micStream = null;
      this.localStream = stream;
      stream.getTracks().forEach(t => this.peerConnection.addTrack(t, stream));

      this.dataChannel = this.peerConnection.createDataChannel('oai-events');

      this.dataChannel.onopen = () => {
        if (this.onOpen) this.onOpen();
      };
      this.dataChannel.onmessage = e => {
        if (this.onMessage) this.onMessage(e);
      };
      this._watch(this.peerConnection, this.dataChannel, gen);

      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      if (this.beforeCalls) await this.beforeCalls();
      if (gen !== this._gen) {
        // disconnect() ran while we were waiting — don't open a stray session
        stream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
        try { this.peerConnection?.close(); } catch (_) {}
        return;
      }

      // Build session for /calls — include full config if available
      const sessionObj = Object.assign({
        type: 'realtime',
        model: MODEL,
      }, this.sessionConfig || {});

      const formData = new FormData();
      formData.set('sdp', offer.sdp);
      formData.set('session', JSON.stringify(sessionObj));

      const sdpRes = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${ephemeral_key}`,
          },
          body: formData
        }
      );

      const sdpAnswer = await sdpRes.text();
      if (!sdpRes.ok) {
        throw new Error(`WebRTC SDP exchange failed: ${sdpAnswer}`);
      }

      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: sdpAnswer
      });
      if (gen !== this._gen) return; // Pause pressed during the SDP exchange

      this.isConnected = true;
      this.emitEvent('connection:status', 'connected');
    } catch (err) {
      console.error(err);
      this.emitEvent('connection:error', err);
    }
  }

  disconnect() {
    this._gen++;
    clearTimeout(this._dropTimer);
    this._dropTimer = null;
    try {
      this.peerConnection?.close();
      this.dataChannel?.close();
      this.audioElement && (this.audioElement.srcObject = null);
    } catch {}
    // Closing the peer connection doesn't release the microphone — without
    // this the browser's mic indicator stayed on after Pause.
    [this.localStream, this.micStream].forEach(s => {
      if (s) s.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
    });
    this.localStream = null;
    this.micStream = null;
    this.isConnected = false;
    this.emitEvent('connection:status', 'disconnected');
  }

  isInit() {
    return this.isConnected && this.dataChannel && this.dataChannel.readyState === 'open';
  }
}

export default ConnectionManager;
